// src/lib/hoverCard.js
//
// The HoverCard primitive's state machine (components/ui/hover-card.js),
// timers injected so it is testable without a DOM (Deno).
//   · hover opens after HOVER_OPEN_DELAY_MS; a quick pass-through never opens
//   · trigger + card are ONE region: moving between them inside the close
//     delay keeps it open
//   · focus and Enter / click open at once (keyboard users do not wait)
//   · Escape closes, returns focus to the trigger, and suppresses a reopen
//     until the pointer has left (the pointer may still rest on the trigger)
//   · an outside click or a pointer leave closes WITHOUT moving focus
//   · blur closes unless focus moved into the card (related target)
//   · while keyboard focus is inside the region, a pointer leave does not close it
// onChange(open, { returnFocus }) is called on every change.

export const HOVER_OPEN_DELAY_MS = 120;
export const HOVER_CLOSE_DELAY_MS = 150;

export function createHoverCard({ schedule, cancel, onChange, openDelay = HOVER_OPEN_DELAY_MS, closeDelay = HOVER_CLOSE_DELAY_MS }) {
  let open = false, timer = null, suppressed = false, overTrigger = false, overContent = false, focusInside = false;
  const clear = () => { if (timer !== null) { cancel(timer); timer = null; } };
  const set = (next, returnFocus = false) => {
    clear();
    if (next === open) return;
    open = next;
    onChange?.(open, { returnFocus });
  };
  const later = (fn, ms) => { clear(); timer = schedule(() => { timer = null; fn(); }, ms); };
  const maybeClose = () => { if (!overTrigger && !overContent && !focusInside) later(() => set(false), closeDelay); };
  return {
    isOpen: () => open,
    pointerEnter() {
      overTrigger = true;
      if (suppressed) return;
      if (open) clear(); else later(() => set(true), openDelay);
    },
    pointerLeave() {
      overTrigger = false;
      suppressed = false;
      if (open) maybeClose(); else clear();
    },
    contentEnter() { overContent = true; if (open) clear(); },
    contentLeave() { overContent = false; if (open) maybeClose(); },
    focus() { if (!suppressed) set(true); },
    /** keyboard focus entered the region (trigger or card): it stays open while focus is inside */
    focusIn() { focusInside = true; clear(); },
    /** focus left an element; intoRegion = it moved to another element of the region */
    focusOut({ intoRegion = false } = {}) {
      if (intoRegion) return;
      focusInside = false;
      if (open && !overTrigger && !overContent) later(() => set(false), closeDelay);
    },
    blur({ intoContent = false } = {}) { if (!intoContent && open) set(false); },
    toggle() { suppressed = false; set(!open, open); },
    escape() { if (!open) return; suppressed = overTrigger; focusInside = false; set(false, true); },
    outsideClick() { focusInside = false; if (open) set(false); },
    dispose() { clear(); },
  };
}
